import { useDispatch, useSelector } from "react-redux";
import "./topbar.scss";
import { Button, Form } from "react-bootstrap";
import { OAuth2Service } from "services/oauth2.service";
import { useTranslation } from "react-i18next";
import { IoClose, IoArrowBackSharp, IoSearch } from "react-icons/io5";
import { forwardRef, useEffect, useRef, useState } from "react";
import i18n from 'translation/i18n';
import { Link, useNavigate } from "react-router-dom";
import useEscapeKey from "hook/useEscapeKey";
import { useFocusTrap } from "hook/useFocusTrap";
import OpenSession from "parts/openSession/openSession";
import { setFilteredData, setSearchValue } from "store/titleBar.slice";
import type { activityType } from "models/activity.model";
import { icons } from "assets";
import { setTheme } from "store/theme.slice";
import { GrLanguage } from "react-icons/gr";
import { MdOutlinePalette } from "react-icons/md";
import { Dropdown } from 'react-bootstrap';
import { IoChevronDownSharp } from "react-icons/io5";
import { IoIosUnlock } from "react-icons/io";

interface userType {
    lastname: string,
    firstname: string,
}
export interface titleBarType {
    title: string,
    data: activityType[],
    filteredData: activityType[],
    isBackButton: boolean,
    isSearchBar: boolean,
    backButtonLabel: string,
    overrideSearchFn: boolean,
}

const Topbar = forwardRef<HTMLDivElement>((props, ref) => {
    const theme = useSelector((state: any) => state.theme);
    const [showUserData, setShowUserData] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [langSelected, setLangSelected] = useState(localStorage.getItem('lang') ?? "fr");
    const [subThemeSelected, setSubThemeSelected] = useState(localStorage.getItem('subTheme') ?? 'peacock');
    const oauth2Service = new OAuth2Service();
    const { t } = useTranslation();
    const user: userType = useSelector((state: any) => state.user);
    const titleBar: titleBarType = useSelector((state: any) => state.titleBar);
    const userContainerRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState<string>("");
    const dispatch = useDispatch();
    const IconUser = icons['user'];
    const inputRef = useRef<HTMLInputElement | null>(null);

    function onSearchClick(): void {
        if (theme.isTabletPortraitOrMobile && !isSearchOpen) {
            setIsSearchOpen(true);
        } else {
            setSearchText("");
            dispatch(setSearchValue({ searchValue: ""}));
            search("");
        }
    }

    function search(value: string): void {
        setSearchText(value);
        dispatch(setSearchValue({ searchValue: value}));

        if(!titleBar.overrideSearchFn){
            const txt = value.toLowerCase()
            const filteredData = titleBar.data.filter((activity) => activity.title.toLowerCase().includes(txt));
            dispatch(setFilteredData({ filteredData: filteredData }));
        }
    }

    useEscapeKey(() => setShowUserData(false));
    useFocusTrap(userContainerRef, showUserData);

    useEffect(() => {
        if (isSearchOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isSearchOpen]);

    useEffect(() => {
        localStorage.setItem('lang', langSelected);
        i18n.changeLanguage(langSelected);
        dispatch(setTheme(
            { lang: langSelected }
        ));
    }, [langSelected]);

    useEffect(() => {
        if (!subThemeSelected) return;
        localStorage.setItem('subTheme', subThemeSelected);
        dispatch(setTheme(
            { subTheme: subThemeSelected }
        ));
    }, [subThemeSelected]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (userContainerRef.current && !userContainerRef.current.contains(event.target as Node)) {
                setShowUserData(false);
            }
        }

        if (showUserData) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showUserData, setShowUserData]);


    const handleKeyDownToggleUser = (e: React.KeyboardEvent<SVGElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowUserData(!showUserData)
        }
    };

    return (
        <div className="topBar__container">
            {!isSearchOpen && <div className="topBar__title">
                <h4 ref={ref} tabIndex={-1}>
                    {t(titleBar.title) || t("loading")}
                </h4>
            </div>}

            {titleBar.isBackButton && !isSearchOpen &&
                <Button variant="primary" size="sm" className="topBar__back__button btn_accent" onClick={() => navigate(-1)}>
                    <IoArrowBackSharp />
                    {t(titleBar.backButtonLabel)}
                </Button>
            }

            {titleBar.isSearchBar &&
                <>
                    <search className={`topBar__searchBar ${isSearchOpen ? 'fullWidth' : ''}`}>
                        {isSearchOpen && 
                            <Button 
                                variant="light" 
                                size="sm" 
                                className="topBar__searchBar__close" 
                                onClick={() => setIsSearchOpen(false)}>
                                    <IoArrowBackSharp />
                            </Button>
                        }
                        {(!theme.isTabletPortraitOrMobile || isSearchOpen) && 
                            <Form.Control 
                                ref={inputRef}
                                id="inputSearch"
                                className="topBar__searchBar__input" 
                                name={t("searchBar")} 
                                aria-describedby="search-instructions" 
                                type="text" 
                                placeholder={t("searchBar")} 
                                value={searchText} 
                                onChange={(e) => search(e.target.value)} />
                        }
                        <Button 
                            variant="outline-secondary" 
                            className="topBar__searchBar__button" 
                            aria-label={t("clear_search")} 
                            onClick={onSearchClick}>
                                {searchText && searchText !== "" ? <IoClose /> : <IoSearch />}
                        </Button>
                    </search>
                    <span id="search-instructions" className="screen_reader-only">
                        {t("search_instruction")}
                    </span>
                </>
            }
            {user && !isSearchOpen &&
                <div className="user_block">
                    {!theme.isTabletPortraitOrMobile &&
                        <div className="user_block__name">
                            <span>{t("welcome")}</span>
                            <span>{user.lastname}</span>
                            <span>{user.firstname}</span>
                        </div>
                    }

                    <div className="user_block__container">
                        <IconUser
                            className="user_block__icon"
                            aria-label={t("show_user_pref")}
                            aria-haspopup="menu"
                            aria-expanded={showUserData}
                            aria-controls="user_block__data"
                            tabIndex={0}
                            role="button"
                            onClick={() => setShowUserData(!showUserData)} onKeyDown={handleKeyDownToggleUser} />

                        {showUserData &&
                            <div
                                ref={userContainerRef}
                                id="user_block__data"
                                className="user_block__data"
                                role="menu"
                                aria-label={t("user-prefs")}>

                                <IoClose
                                    className="user_block__data__close"
                                    tabIndex={0}
                                    role="button"
                                    aria-label={t("hide_user_pref")}
                                    onClick={() => setShowUserData(false)}
                                    onKeyDown={handleKeyDownToggleUser} />

                                {theme.isTabletPortraitOrMobile &&
                                    <>
                                        <div className="user_block__name">
                                            <span>{t("welcome")}</span>
                                            <span className="bold">{`${user.lastname} ${user.firstname}`}</span>
                                        </div>

                                        <OpenSession />
                                    </>
                                }
                                <div>
                                    <label htmlFor="user__lang" className="form-label">
                                        <GrLanguage /> {t("lang")}
                                    </label>
                                    <Dropdown>
                                        <Dropdown.Toggle variant="light" className="dropdown-select rounded" id="user__lang" aria-label={t("lang_selector")}>
                                            {t(langSelected) || 'Langue'}
                                            <IoChevronDownSharp size={16} />
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu>
                                            <Dropdown.Item onClick={() => setLangSelected('fr')}>{t('fr')}</Dropdown.Item>
                                            <Dropdown.Item onClick={() => setLangSelected('en')}>{t('en')}</Dropdown.Item>
                                            <Dropdown.Item onClick={() => setLangSelected('ar')}>{t('ar')}</Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                                <div>
                                    <label htmlFor="user__theme" className="form-label">
                                        <MdOutlinePalette /> {t("theme")}
                                    </label>
                                    <Dropdown>
                                        <Dropdown.Toggle variant="light" className="dropdown-select rounded" id="user__theme" aria-label={t("theme_selector")}>
                                            {t(subThemeSelected)}
                                            <IoChevronDownSharp size={16} />
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu>
                                            <Dropdown.Item onClick={() => setSubThemeSelected('dragonfly')}>{t('dragonfly')}</Dropdown.Item>
                                            <Dropdown.Item onClick={() => setSubThemeSelected('peacock')}>{t('peacock')}</Dropdown.Item>
                                            <Dropdown.Item onClick={() => setSubThemeSelected('humming-bird')}>{t('humming-bird')}</Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>

                                <Button className="user_block__data__logout" variant="dark" size="sm" role="menuitem" onClick={oauth2Service.logout}>
                                    <IoIosUnlock />
                                    {t('logout')}
                                </Button>

                                <Link className="user_block__data__legals" to="/legals">
                                    {t("legals")}
                                </Link>

                            </div>
                        }
                    </div>
                </div>
            }
        </div>
    );
})
export default Topbar;
